<script setup lang="ts">
import { AppButton, AppTypography } from '@/core/components';

const props = withDefaults(
  defineProps<{
    errorMessages?: string;
    resendTime?: number;
    length?: number;
    isResendSuccess?: boolean;
    showResend?: boolean;
    loading?: boolean;
  }>(),
  {
    errorMessages: undefined,
    resendTime: 300, // 5 mins
    length: 5,
    isResendSuccess: false,
    showResend: true,
    loading: false,
  }
);

const emits = defineEmits<{
  (e: 'resend'): void;
}>();

// Timer state management with localStorage persistence
const timerRunning = ref(false);
const timer = ref(props.resendTime);
let timerInterval: any = null;

// Check if there's an existing timer in localStorage
const checkExistingTimer = () => {
  if (typeof window !== 'undefined') {
    const storedTimer = localStorage.getItem('otp-timer-end');
    if (storedTimer) {
      const endTime = parseInt(storedTimer);
      const now = Date.now();
      const remainingTime = Math.ceil((endTime - now) / 1000);
      
      if (remainingTime > 0) {
        timer.value = remainingTime;
        timerRunning.value = true;
        startTimer();
      } else {
        // Timer has expired, clear localStorage
        localStorage.removeItem('otp-timer-end');
      }
    }
  }
};

// Start the timer and store end time in localStorage
const startTimer = () => {
  if (typeof window !== 'undefined') {
    // Clear any existing interval
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    
    const endTime = Date.now() + (timer.value * 1000);
    localStorage.setItem('otp-timer-end', endTime.toString());
    
    timerInterval = setInterval(() => {
      timer.value--;
      if (timer.value <= 0) {
        if (timerInterval) {
          clearInterval(timerInterval);
          timerInterval = null;
        }
        timerRunning.value = false;
        localStorage.removeItem('otp-timer-end');
      }
    }, 1000);
  }
};

// Initialize timer on component mount
onMounted(() => {
  checkExistingTimer();
});

// Clean up timer interval on component unmount
onUnmounted(() => {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
});

watch(
  () => props.isResendSuccess,
  () => {
    if (props.isResendSuccess) {
      timer.value = props.resendTime;
      timerRunning.value = true;
      startTimer();
    }
  }
);

const formattedTime = computed(() => {
  const minutes = Math.floor(timer.value / 60);
  const seconds = timer.value % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

defineExpose({
  formattedTime,
  timerRunning,
});

const otp = defineModel<string>({
  default: '',
});
</script>

<template>
  <div class="otp-container">
    <v-otp-input
      v-model="otp"
      variant="solo-filled"
      data-testid="app-otp"
      data-cy="input-otp"
      :length="length"
      :error="Boolean(errorMessages)"
      autofocus
    />
    <v-messages class="mt-1" color="error" :active="Boolean(errorMessages?.length)" :messages="errorMessages" />
    <div v-if="showResend" class="otp-request text-center d-flex justify-center align-center gap-2">
      <AppTypography element="span" type="body-2">Didn't receive the code?</AppTypography>
      <AppButton
        v-if="!timerRunning"
        data-testid="resend-otp"
        class="pa-0 font-weight-bold"
        variant="plain"
        type="button"
        @click="emits('resend')"
        :loading="loading"
      >
        Resend
      </AppButton>
      <div v-else>
        <AppTypography class="text-disabled font-weight-bold" element="span" type="headline-1">
          Resend in {{ formattedTime }}
        </AppTypography>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.v-otp-input {
  :deep(.v-otp-input__content) {
    padding: 0;
  }
}

:deep(.v-otp-input .v-otp-input__content) {
  width: 100%;
  max-width: 100% !important;
  gap: 16px !important;
  height: 56px !important;
  gap: 8px;

  .v-otp-input__field {
    border-radius: 8px;
    font-family: 'Excon' !important;
    font-size: 22px !important;
    font-weight: 700 !important;
    line-height: 28px !important;
    text-align: center !important;
  }
}
.otp-request {
  gap: 8px;
}

.v-messages {
  opacity: 1;
}
</style>
